import React from 'react';
import moment from 'moment';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import SidebarLogo from '../Image/Everyday -Logo.png';

const colors = {
  pageBg: '#EEF6FB',
  cardBg: '#ffffff',
  border: '#B8E0F0',
  primary: '#3D0012',
  title: '#3D0012',
  textDark: '#2c3e50',
  textMuted: '#7f8c8d',
  label: '#6c7a89',
  sectionAccent: '#C51020',
  rowTint: '#E8F4FA',
  rowHighlight: '#FBC02D40',
  netRow: '#7EC8E340',
};

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: colors.pageBg,
  },
  container: {
    maxWidth: '100%',
  },
  header: {
    alignItems: 'center',
    marginBottom: 14,
  },
  logo: {
    width: 90,
    objectFit: 'contain',
    marginBottom: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.title,
    textAlign: 'center',
  },
  row: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 10,
  },
  card: {
    flex: 1,
    backgroundColor: colors.cardBg,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    padding: 10,
  },
  cardLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.sectionAccent,
    marginBottom: 4,
  },
  cardValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.textDark,
    marginBottom: 2,
  },
  cardMuted: {
    fontSize: 9,
    color: colors.textMuted,
    marginBottom: 2,
  },
  sectionCard: {
    backgroundColor: colors.cardBg,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: 10,
    overflow: 'hidden',
  },
  sectionHeader: {
    padding: 8,
    backgroundColor: colors.cardBg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.sectionAccent,
  },
  table: {
    width: '100%',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
  },
  thCell: {
    padding: 6,
    color: '#ffffff',
    fontSize: 9,
    fontWeight: 'bold',
  },
  tdCell: {
    padding: 6,
    fontSize: 9,
    color: colors.textDark,
  },
  tdCellTint: {
    padding: 6,
    fontSize: 9,
    color: colors.textDark,
    backgroundColor: colors.rowHighlight,
  },
  flex4: { flex: 4 },
  flex2: { flex: 2 },
  flex1: { flex: 1 },
  textLeft: { textAlign: 'left' },
  textCenter: { textAlign: 'center' },
  textRight: { textAlign: 'right' },
  netRow: {
    flexDirection: 'row',
    backgroundColor: colors.netRow,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  netLabel: {
    flex: 5,
    padding: 8,
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.textDark,
    textAlign: 'left',
  },
  netValue: {
    flex: 1,
    padding: 8,
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.textDark,
    textAlign: 'right',
  },
  noObjectionWrap: {
    backgroundColor: colors.cardBg,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: 10,
    overflow: 'hidden',
  },
  noObjectionHeader: {
    padding: 8,
    backgroundColor: colors.primary,
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  noObjectionBody: {
    padding: 14,
    fontSize: 9,
    color: colors.textDark,
    lineHeight: 1.5,
  },
  bold: {
    fontWeight: 'bold',
  },
  signatureSection: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
    marginBottom: 10,
  },
  signatureHeaderRow: {
    flexDirection: 'row',
    backgroundColor: colors.rowTint,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  signatureLabelCell: {
    flex: 1,
    padding: 8,
    borderRightWidth: 1,
    borderRightColor: colors.border,
  },
  signatureLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    color: colors.label,
  },
  signatureValueRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  signatureValueCell: {
    flex: 1,
    padding: 8,
    borderRightWidth: 1,
    borderRightColor: colors.border,
    minHeight: 18,
  },
  signatureValue: {
    fontSize: 9,
    color: colors.textDark,
  },
  signatureImageRow: {
    flexDirection: 'row',
  },
  signatureImageCell: {
    flex: 1,
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: colors.border,
    minHeight: 55,
    alignItems: 'center',
    justifyContent: 'center',
  },
  signatureImage: {
    width: '75%',
    maxHeight: 50,
    objectFit: 'contain',
  },
  footer: {
    marginTop: 4,
    padding: 10,
    backgroundColor: colors.primary,
    borderRadius: 8,
    alignItems: 'center',
  },
  footerBrand: {
    color: '#ffffff',
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  footerThanks: {
    color: '#ffffff',
    fontSize: 9,
  },
  attachmentPage: {
    padding: 20,
    backgroundColor: colors.pageBg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  attachmentImage: {
    width: '90%',
    maxHeight: '95%',
    objectFit: 'contain',
  },
});

const InfoCard = ({ label, lines }) => (
  <View style={styles.card}>
    <Text style={styles.cardLabel}>{label}</Text>
    {lines.map((line, i) =>
      line.bold ? (
        <Text key={i} style={styles.cardValue}>{line.text}</Text>
      ) : (
        <Text key={i} style={styles.cardMuted}>{line.text}</Text>
      )
    )}
  </View>
);

const PdfTemplates = ({ voucherData }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Image src={SidebarLogo} style={styles.logo} />
          <Text style={styles.title}>WINNING GIFT VOUCHER</Text>
        </View>

        <View style={styles.row}>
          <InfoCard
            label="PAYMENT TO"
            lines={[
              { bold: true, text: voucherData?.payment_to || '—' },
              { bold: false, text: `Seller: ${voucherData?.seller_name || '—'}` },
              { bold: false, text: `${voucherData?.document_type || 'EID/Passport'}: ${voucherData?.document_no || '—'}` },
            ]}
          />
          <InfoCard
            label="VOUCHER DETAILS"
            lines={[
              { bold: true, text: `#${voucherData?.voucher_no || '—'}` },
              { bold: false, text: `Date: ${voucherData?.date ? moment(voucherData.date).format('DD-MM-YYYY') : '—'}` },
              { bold: false, text: `Purchase: ${voucherData?.purchase_date ? moment(voucherData.purchase_date).format('DD-MM-YYYY') : '—'}` },
            ]}
          />
        </View>

        <View style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Ticket Information</Text>
          </View>
          <View style={styles.tableRow}>
            <View style={[styles.tdCellTint, styles.flex2, styles.textLeft]}>
              <Text style={styles.bold}>Ticket ID</Text>
              <Text>{voucherData?.order_no || '—'}</Text>
            </View>
            <View style={[styles.tdCellTint, styles.flex2, styles.textLeft]}>
              <Text style={styles.bold}>Product Name</Text>
              <Text>{voucherData?.game_name || '—'}</Text>
            </View>
            <View style={[styles.tdCellTint, styles.flex2, styles.textLeft]}>
              <Text style={styles.bold}>Contact No.</Text>
              <Text>{voucherData?.contact_no || '—'}</Text>
            </View>
          </View>
        </View>

        <View style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Summary</Text>
          </View>
          <View style={styles.table}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.thCell, styles.flex4, styles.textLeft]}>Description</Text>
              <Text style={[styles.thCell, styles.flex1, styles.textCenter]}>No. of winning</Text>
              <Text style={[styles.thCell, styles.flex1, styles.textRight]}>Amount</Text>
            </View>
            {voucherData?.winnerList?.length > 0 &&
              voucherData.winnerList.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={[styles.tdCell, styles.flex4, styles.textLeft]}>
                    Winning Amount-{item?.amount || ''}
                  </Text>
                  <Text style={[styles.tdCell, styles.flex1, styles.textCenter]}>{item?.qty || ''}</Text>
                  <Text style={[styles.tdCell, styles.flex1, styles.textRight]}>QR {item?.total || ''}</Text>
                </View>
              ))}
            <View style={styles.netRow}>
              <Text style={styles.netLabel}>Net amount</Text>
              <Text style={styles.netValue}>QR {voucherData?.total_amount || ''}</Text>
            </View>
          </View>
        </View>

        <View style={styles.noObjectionWrap}>
          <Text style={styles.noObjectionHeader}>No Objection Letter</Text>
          <View style={styles.noObjectionBody}>
            <Text>
              I <Text style={styles.bold}>{voucherData?.payment_to || ''}</Text> holding{' '}
              {voucherData?.document_type || ''} : <Text style={styles.bold}>{voucherData?.document_no || ''}</Text> do
              hereby declare and confirm that I have received the above amount as cash from Crown King.
            </Text>
          </View>
        </View>

        <View style={styles.signatureSection}>
          <View style={styles.signatureHeaderRow}>
            {['Received By', 'Prepared By', 'Checked By', 'Authorized By'].map((label) => (
              <View key={label} style={styles.signatureLabelCell}>
                <Text style={styles.signatureLabel}>{label}</Text>
              </View>
            ))}
          </View>
          <View style={styles.signatureValueRow}>
            <View style={styles.signatureValueCell}>
              <Text style={styles.signatureValue}>{voucherData?.received_by || ''}</Text>
            </View>
            <View style={styles.signatureValueCell}>
              <Text style={styles.signatureValue}>{voucherData?.prepared_by || ''}</Text>
            </View>
            <View style={styles.signatureValueCell}>
              <Text style={styles.signatureValue}>{voucherData?.checked_by || ''}</Text>
            </View>
            <View style={styles.signatureValueCell}>
              <Text style={styles.signatureValue}>{voucherData?.authorized_by || ''}</Text>
            </View>
          </View>
          <View style={styles.signatureImageRow}>
            <View style={styles.signatureImageCell}>
              {voucherData?.signature?.signature ? (
                <Image src={voucherData.signature.signature} style={styles.signatureImage} />
              ) : null}
            </View>
            <View style={styles.signatureImageCell} />
            <View style={styles.signatureImageCell} />
            <View style={styles.signatureImageCell} />
          </View>
        </View>

        <View style={styles.footer}>
          {/* <Text style={styles.footerBrand}>Inst Win</Text> */}
          <Text style={styles.footerThanks}>Thank You</Text>
        </View>
      </View>
    </Page>

    {voucherData?.doc?.doc && (
      <Page size="A4" style={styles.attachmentPage}>
        <Image src={voucherData.doc.doc} style={styles.attachmentImage} />
      </Page>
    )}
    {voucherData?.order_slip?.order_slip && (
      <Page size="A4" style={styles.attachmentPage}>
        <Image src={voucherData.order_slip.order_slip} style={styles.attachmentImage} />
      </Page>
    )}
    {voucherData?.redeem_slip?.redeem_slip && (
      <Page size="A4" style={styles.attachmentPage}>
        <Image src={voucherData.redeem_slip.redeem_slip} style={styles.attachmentImage} />
      </Page>
    )}
    {voucherData?.other_attachment?.other_attachment && (
      <Page size="A4" style={styles.attachmentPage}>
        <Image src={voucherData.other_attachment.other_attachment} style={styles.attachmentImage} />
      </Page>
    )}
    {voucherData?.images?.images && (
      <Page size="A4" style={styles.attachmentPage}>
        <Image src={voucherData.images.images} style={styles.attachmentImage} />
      </Page>
    )}
    {voucherData?.images2?.images2 && (
      <Page size="A4" style={styles.attachmentPage}>
        <Image src={voucherData.images2.images2} style={styles.attachmentImage} />
      </Page>
    )}
  </Document>
);

export default PdfTemplates;
